import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import toast from "react-hot-toast";
import { api, getErrorMessage } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { useAuth } from "../../../contexts/AuthContext.tsx";

const MIN_LENGTH = 8;

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Old password, new one twice. Saving signs out other sessions and disconnects connected apps. */
export default function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError("");
  };
  const close = () => {
    reset();
    onClose();
  };

  const change = useMutation({
    mutationFn: () => api.changePassword({ current_password: current, new_password: next }),
    meta: { suppressErrorToast: true },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: keys.account.sessions() });
      queryClient.invalidateQueries({ queryKey: keys.account.connectedApps() });
      await refreshUser();
      toast.success("Password changed");
      close();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== next;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (next.length < MIN_LENGTH || next !== confirm) return;
    change.mutate();
  };

  return (
    <Dialog open={open} onClose={change.isPending ? undefined : close} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>Change password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Your other sessions are signed out, and connected apps such as Claude have to connect again.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoFocus
              size="small"
            />
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              size="small"
              error={tooShort}
              helperText={`At least ${MIN_LENGTH} characters. A few unrelated words make a strong one.`}
            />
            <TextField
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              size="small"
              error={mismatch}
              helperText={mismatch ? "Doesn't match the new password" : " "}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={change.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={change.isPending}
            disabled={!current || next.length < MIN_LENGTH || next !== confirm}
          >
            Change password
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
